import { LEGACY_ARTICLES } from '../content/legacyArticles';

export const SITE_ORIGIN = 'https://lukos.com.br';

export type PageSeo = {
  path: string;
  title: string;
  description: string;
  heading: string;
};

export const INDEXABLE_PAGES: PageSeo[] = [
  {
    path: '/',
    title: 'Lukos | Gestão para postos de combustível',
    description:
      'Potencialize a gestão do seu posto de combustível com a Lukos. Tenha total controle do seu negócio em tempo real, elimine dores de cabeça com o fisco e foque em lucrar.',
    heading: 'Potencialize a gestão do seu posto de combustível com a Lukos',
  },
  {
    path: '/blog',
    title: 'Lukos | Blog',
    description:
      'Insights, tendências e novidades sobre tecnologia e gestão para revolucionar o seu negócio.',
    heading: 'Blog Lukos',
  },
  {
    path: '/sobre-nos',
    title: 'Lukos | Sobre nós',
    description:
      'Com mais de 10 anos de experiência, somos especialistas em desenvolver soluções tecnológicas que transformam a gestão de postos de combustíveis e varejo.',
    heading: 'Lukos Tecnologia',
  },
  {
    path: '/solucoes',
    title: 'Lukos | Soluções',
    description:
      'Consulte dados e peça tarefas do ERP em linguagem natural. A LUK respeita as permissões de cada usuário e só executa depois da sua confirmação.',
    heading: 'Soluções Lukos',
  },
  {
    path: '/contato',
    title: 'Lukos | Contato',
    description:
      'Converse com um especialista Lukos e descubra como integrar PDV, conciliação bancária e fidelidade em uma única plataforma.',
    heading: 'Automatize seu posto. Escale seus resultados.',
  },
  {
    path: '/erp',
    title: 'Lukos | ERP',
    description:
      'O ERP que centraliza sua gestão e acelera suas vendas. Controle estoque, financeiro, fiscal e vendas multicanal em um único lugar.',
    heading: 'O ERP que centraliza sua gestão e acelera suas vendas',
  },
  {
    path: '/lukos-pdv',
    title: 'Lukos | PDV',
    description:
      'Aumente a eficiência operacional e elimine filas com o PDV Lukos. Desenvolvido para a pista e a conveniência, com transações instantâneas e integração total.',
    heading: 'Performance e agilidade no ponto de venda',
  },
  {
    path: '/lukos-ai',
    title: 'Lukos | LUK',
    description:
      'Consulte dados e peça tarefas do ERP em linguagem natural. A LUK respeita as permissões de cada usuário e só executa depois da sua confirmação.',
    heading: 'Inteligência que antecipa. Gestão que escala.',
  },
  {
    path: '/lukos-conciliacao',
    title: 'Lukos | Conciliação',
    description:
      'Elimine o erro humano e recupere lucros perdidos com a conciliação automatizada da Lukos.',
    heading: 'Sua saúde financeira sob controle total',
  },
  {
    path: '/lukos-fidelidade',
    title: 'Lukos | Fidelidade',
    description:
      'Aumente o retorno dos seus clientes com um programa de fidelidade moderno. Ofereça cashback automático e campanhas personalizadas.',
    heading: 'Fidelize com cashback real',
  },
  {
    path: '/lukos-pay',
    title: 'Lukos | Lukos Pay',
    description:
      'Pagamento e gestão em um único lugar. O Lukos Pay integra suas vendas de pista diretamente com o financeiro.',
    heading: 'A maquininha inteligente do seu posto',
  },
  {
    path: '/lukos-dashboard',
    title: 'Lukos | Dashboard',
    description: 'Tome decisões com dados em tempo real com o dashboard Lukos.',
    heading: 'Tome decisões com dados em tempo real',
  },
  ...LEGACY_ARTICLES.map((article) => ({
    path: article.path,
    title: article.title,
    description: article.description,
    heading: article.heading,
  })),
];

export const EXISTING_STATIC_ROUTES = [
  '/tutoriais',
  '/video-tutoriais',
  '/cadastros',
  '/pages/GeradorSenha',
  '/pages/PrecoCombustivel',
  '/nova-pagina',
  '/conciliacao',
  '/fullcheck',
  '/fidelidade',
  '/lms-demo',
  '/ia',
  '/servicos',
  '/apps',
  '/ver',
  '/apresentacao-sistemas',
  '/sistemas',
  '/produtos',
  '/register',
] as const;

export const PRIVATE_ROUTES = ['/admin', '/profile', '/editor'] as const;

export const PRIVATE_PATH_PREFIXES = ['/admin', '/profile', '/editor', '/register', '/wp-admin'];

export const SPA_PREFIXES = ['/tutoriais', '/admin'] as const;

export const EXISTING_PREFIXES = [
  ...SPA_PREFIXES,
  '/blog',
  '/artigo',
  '/produto',
  '/video-tutorial',
  '/categoria',
  '/pages',
] as const;

export const PERMANENT_REDIRECTS: Array<{ from: string; to: string }> = [
  { from: '/blog-posts', to: '/blog' },
  { from: '/retaguarda-tutoriais', to: '/tutoriais' },
  { from: '/conveniencia-tutoriais', to: '/tutoriais' },
  { from: '/dashboard-tutoriais', to: '/tutoriais' },
  { from: '/pista-tutoriais', to: '/tutoriais' },
];

export const NOT_FOUND_SEO: PageSeo = {
  path: '/404',
  title: 'Lukos | Página não encontrada',
  description: 'Esta página não existe no site da Lukos.',
  heading: 'Página não encontrada',
};

export const DEFAULT_PAGE_TITLE = INDEXABLE_PAGES[0].title;

const indexableByPath = new Map(INDEXABLE_PAGES.map((page) => [page.path, page]));

export function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

export function canonicalForPath(pathname: string): string {
  const path = normalizePath(pathname);
  return path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`;
}

export function seoForPath(pathname: string): PageSeo {
  const path = normalizePath(pathname);
  return indexableByPath.get(path) ?? {
    path,
    title: DEFAULT_PAGE_TITLE,
    description: INDEXABLE_PAGES[0].description,
    heading: INDEXABLE_PAGES[0].heading,
  };
}

export function isIndexablePath(pathname: string): boolean {
  return indexableByPath.has(normalizePath(pathname));
}

export function isPrivatePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return PRIVATE_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function isKnownStaticPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (indexableByPath.has(path)) return true;
  if ((EXISTING_STATIC_ROUTES as readonly string[]).includes(path)) return true;
  if ((PRIVATE_ROUTES as readonly string[]).includes(path)) return true;
  if (EXISTING_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return true;
  return false;
}

export function robotsForPath(pathname: string, isDev: boolean, isNotFound = false): string {
  if (isDev || isNotFound || isPrivatePath(pathname) || !isIndexablePath(pathname)) {
    return 'noindex, nofollow';
  }
  return 'index, follow';
}

export function titleForPath(pathname: string): string {
  const path = normalizePath(pathname);
  if (indexableByPath.has(path)) return indexableByPath.get(path)!.title;
  if (path.startsWith('/blog')) return indexableByPath.get('/blog')!.title;
  if (path.startsWith('/tutoriais')) return 'Lukos | Tutoriais';
  if (path === '/404' || path === '/nao-encontrado') return NOT_FOUND_SEO.title;
  return DEFAULT_PAGE_TITLE;
}

export const NAV_LINKS = [
  { href: '/', label: 'Início' },
  { href: '/sobre-nos', label: 'Sobre nós' },
  { href: '/solucoes', label: 'Soluções' },
  { href: '/blog', label: 'Blog' },
  { href: '/contato', label: 'Falar com especialista' },
] as const;
